import React, { useCallback, useState } from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import MDButton from "components/MDButton";
import {
  ChangeOrderProposalType,
  ChangeOrderScheduledJobsAction,
  Proposal,
} from "generated/graphql";
import useDuplicateProposal from "hooks/proposals/useDuplicateProposal";
import useHasScheduledJobPhases from "hooks/jobs/useHasScheduledJobPhases";
import { useNavigate } from "react-router";
import { getRoute } from "utils/routing";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

type Step = "choose-type" | "checking-schedule" | "choose-schedule-action" | "executing";

export default function CreateChangeOrder({
  id,
  status,
  changeOrderJobId,
  isChangeOrder,
}: {
  id: Proposal["id"];
  status: Proposal["status"];
  changeOrderJobId: Proposal["changeOrderJobId"];
  isChangeOrder: Proposal["isChangeOrder"];
}) {
  const [duplicateProposal, { isSuccess, getData }] = useDuplicateProposal();
  const [hasScheduledJobPhases] = useHasScheduledJobPhases();
  const validStatuses = ["DRAFT", "SENT", "CLOSED_WON", "CLOSED_LOST", "CANCELED"];
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>("choose-type");
  const [executing, setExecuting] = useState(false);

  const handleClose = useCallback(() => {
    setOpen(false);
    setStep("choose-type");
    setExecuting(false);
  }, []);

  const navigateToProposal = useCallback(
    (proposalId: string) => {
      navigate(getRoute("proposals.draft", [["proposalId", proposalId]]));
    },
    [navigate]
  );

  const handleDuplicateProposal = useCallback(
    async (changeOrderScheduledJobsAction?: ChangeOrderScheduledJobsAction) => {
      setExecuting(true);
      try {
        const variables: Parameters<typeof duplicateProposal>[0]["variables"] = {
          id,
          changeOrderJobId,
          changeOrderProposalType: ChangeOrderProposalType.CHANGE_EXISTING_PROPOSAL,
        };
        if (changeOrderScheduledJobsAction) {
          variables.changeOrderScheduledJobsAction = changeOrderScheduledJobsAction;
        }
        const result = await duplicateProposal({ variables });
        if (isSuccess(result.data)) {
          const data = getData(result);
          handleClose();
          navigateToProposal(data.newProposal.id);
        } else {
          setExecuting(false);
        }
      } catch {
        setExecuting(false);
      }
    },
    [id, changeOrderJobId, duplicateProposal, isSuccess, getData, handleClose, navigateToProposal]
  );

  const handleCreateNewChangeOrderProposal = useCallback(async () => {
    setStep("choose-type");
    setOpen(false);
    setExistingDialogOpen(true);
  }, []);

  const [existingDialogOpen, setExistingDialogOpen] = useState(false);

  const handleExistingMakeChanges = useCallback(async () => {
    setExecuting(true);
    try {
      const result = await duplicateProposal({
        variables: {
          id,
          isChangeOrder: true,
          changeOrderJobId,
        },
      });
      if (isSuccess(result.data)) {
        const data = getData(result);
        setExistingDialogOpen(false);
        setExecuting(false);
        navigateToProposal(data.newProposal.id);
      } else {
        setExecuting(false);
      }
    } catch {
      setExecuting(false);
    }
  }, [id, changeOrderJobId, duplicateProposal, isSuccess, getData, navigateToProposal]);

  const handleExistingAddSeparateJob = useCallback(async () => {
    setExecuting(true);
    try {
      const result = await duplicateProposal({
        variables: {
          id,
          isChangeOrder: true,
          startNewProposal: true,
          changeOrderJobId,
        },
      });
      if (isSuccess(result.data)) {
        const data = getData(result);
        setExistingDialogOpen(false);
        setExecuting(false);
        navigateToProposal(data.newProposal.id);
      } else {
        setExecuting(false);
      }
    } catch {
      setExecuting(false);
    }
  }, [id, changeOrderJobId, duplicateProposal, isSuccess, getData, navigateToProposal]);

  const handleChangeExistingProposal = useCallback(async () => {
    setStep("checking-schedule");
    try {
      const result = await hasScheduledJobPhases({
        variables: { jobId: changeOrderJobId },
      });
      const hasScheduled = result?.data?.hasScheduledJobPhases;
      if (hasScheduled) {
        setStep("choose-schedule-action");
      } else {
        await handleDuplicateProposal();
      }
    } catch {
      setStep("choose-type");
    }
  }, [changeOrderJobId, hasScheduledJobPhases, handleDuplicateProposal]);

  const greyButtonStyle = {
    backgroundColor: "#757575",
    color: "#FFF",
    ":hover": { backgroundColor: "#616161" },
  };

  return (
    <>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        aria-describedby="change-order-type-dialog"
      >
        {step === "choose-type" && (
          <>
            <DialogTitle sx={{ textAlign: "center" }}>Create Change Order</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ textAlign: "center" }}>
                How do you want to create your change order?
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center", gap: 1, px: 3, pb: 2 }}>
              <Button
                onClick={handleCreateNewChangeOrderProposal}
                variant="contained"
                sx={greyButtonStyle}
              >
                Create New Change Order Proposal
              </Button>
              <Button
                onClick={handleChangeExistingProposal}
                variant="contained"
                sx={greyButtonStyle}
              >
                Change Existing Proposal
              </Button>
            </DialogActions>
          </>
        )}

        {step === "checking-schedule" && (
          <>
            <DialogTitle>Checking Schedule...</DialogTitle>
            <DialogContent sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </DialogContent>
          </>
        )}

        {step === "choose-schedule-action" && (
          <>
            <DialogTitle sx={{ textAlign: "center" }}>Scheduled Jobs Found</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ textAlign: "center" }}>
                How do you want to handle currently scheduled jobs?
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center", gap: 1, px: 3, pb: 2 }}>
              <Button
                onClick={() =>
                  handleDuplicateProposal(ChangeOrderScheduledJobsAction.LEAVE_JOBS_SCHEDULED)
                }
                disabled={executing}
                variant="contained"
                sx={greyButtonStyle}
              >
                Leave Jobs Scheduled As Is
              </Button>
              <Button
                onClick={() =>
                  handleDuplicateProposal(ChangeOrderScheduledJobsAction.REMOVE_JOBS_FROM_SCHEDULE)
                }
                disabled={executing}
                variant="contained"
                sx={greyButtonStyle}
              >
                Remove Jobs From Schedule
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog
        open={existingDialogOpen}
        TransitionComponent={Transition}
        keepMounted
        onClose={() => {
          setExistingDialogOpen(false);
          setExecuting(false);
        }}
        aria-describedby="existing-change-order-dialog"
      >
        <DialogTitle sx={{ textAlign: "center" }}>Create Change Order</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ textAlign: "center" }}>
            How do you want to create your change order?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 1, px: 3, pb: 2 }}>
          <Button
            onClick={handleExistingAddSeparateJob}
            disabled={executing}
            variant="contained"
            sx={greyButtonStyle}
          >
            {executing ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Add Separate Job to Scope"
            )}
          </Button>
          <Button
            onClick={handleExistingMakeChanges}
            disabled={executing}
            variant="contained"
            sx={greyButtonStyle}
          >
            {executing ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Make Changes to Existing Proposal"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <MDButton
        sx={{ minWidth: "190px" }}
        disabled={!validStatuses.includes(status) || isChangeOrder}
        variant="gradient"
        color="success"
        onClick={() => setOpen(true)}
      >
        Create Change Order
      </MDButton>
    </>
  );
}
